import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { isAdTagged, trafficSource } from "@/lib/utils";

const STATUSES = ["new", "contacted", "confirmed", "attended", "cancelled"];
const FOLLOWUP_LABEL = { thank_you_sent: "Thank-you sent", missed_you_sent: "Missed-you sent", failed: "Failed, retry", skipped: "Skipped (email not configured)" };

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-[120px_1fr] gap-3 border-b border-white/8 py-2 text-sm last:border-0">
      <span className="text-[11px] font-bold uppercase tracking-wide text-[#f2d28f]/80">{label}</span>
      <span className="break-words text-white/85">{value || "–"}</span>
    </div>
  );
}

export function RegistrationDialog({ registration, onClose, onSave, onFollowup, followupPending }) {
  const [status, setStatus] = useState("new");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (registration) {
      setStatus(registration.status);
      setNotes(registration.notes || "");
    }
  }, [registration]);

  const synced = registration && ["attended", "no_show"].includes(registration.attendance_status) && registration.status !== "cancelled";
  const followupSent = registration?.followup_status?.endsWith("_sent");

  return (
    <Dialog open={Boolean(registration)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-white/10 bg-[#0b211a] text-foreground sm:max-w-xl" data-testid="registration-dialog">
        {registration && (
          <>
            <DialogHeader>
              <p className="admin-kicker">Registration detail</p>
              <DialogTitle className="text-2xl font-bold tracking-tight">{registration.full_name}</DialogTitle>
              <DialogDescription className="text-white/50">Registered {dayjs(registration.created_at).format("DD MMM YYYY, HH:mm")}</DialogDescription>
            </DialogHeader>
            <div className="rounded-xl border border-white/10 bg-white/[0.03] px-4 py-1">
              <Row label="Email" value={registration.email} />
              <Row label="Mobile" value={registration.mobile} />
              <Row label="Session" value={`${registration.event_date} · ${registration.event_time}`} />
              <Row label="Language" value={registration.event_language} />
              <Row label="Venue" value={registration.event_venue} />
              <Row label="Question" value={registration.main_question} />
              <Row label="Age range" value={registration.age_range} />
              <Row label="Referral" value={registration.ref_code ? `${registration.ref_code} · ${registration.referrer_name || "Unknown link"}` : "Direct / organic"} />
              <Row label="Invited by" value={registration.invited_by} />
              <Row label="Source" value={registration.registration_source?.replace(/_/g, " ")} />
              <Row label="Traffic" value={<span data-testid="dialog-traffic-source">{trafficSource(registration)}</span>} />
              {isAdTagged(registration) && (
                <>
                  <Row label="Campaign" value={<span data-testid="dialog-utm-campaign">{registration.utm_campaign}</span>} />
                  <Row label="Ad content" value={registration.utm_content} />
                  <Row label="Keyword" value={registration.utm_term} />
                  <Row label="FB click ID" value={registration.fbclid ? <span className="break-all text-xs text-white/60">{registration.fbclid}</span> : ""} />
                </>
              )}
              <Row label="Zapier" value={registration.zapier_status} />
              <Row label="Email" value={registration.email_status} />
              <Row label="Zoom" value={registration.zoom_status} />
              {registration.zoom_join_url && (
                <Row
                  label="Join link"
                  value={
                    <a
                      href={registration.zoom_join_url}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-[#f2d28f] underline hover:text-[#f7deae]"
                      data-testid="dialog-zoom-join-link"
                    >
                      {registration.zoom_join_url}
                    </a>
                  }
                />
              )}
              <Row label="Reminder" value={registration.reminder_status} />
              <Row
                label="Attendance"
                value={
                  registration.attendance_status && registration.attendance_status !== "pending"
                    ? registration.attendance_status === "attended"
                      ? `Attended${registration.attended_duration_minutes ? ` · ${registration.attended_duration_minutes} min` : ""}`
                      : "No-show"
                    : "Not synced yet"
                }
              />
              <Row
                label="Follow-up"
                value={
                  <span className="flex flex-wrap items-center gap-2" data-testid="dialog-followup-status">
                    <span>{FOLLOWUP_LABEL[registration.followup_status] || (synced ? "Not sent yet" : "Available after attendance sync")}{followupSent && registration.followup_sent_at ? ` · ${dayjs(registration.followup_sent_at).format("DD MMM, HH:mm")}` : ""}</span>
                    {synced && (
                      <Button size="sm" variant="outline" disabled={followupPending} onClick={onFollowup} className="h-7 rounded-full border-white/15 bg-white/5 px-3 text-[11px] font-semibold hover:bg-[#f2d28f] hover:text-[#07130f]" data-testid="dialog-send-followup-btn">
                        <Send className="mr-1 h-3 w-3" /> {followupPending ? "Sending…" : followupSent ? "Re-send follow-up" : registration.attendance_status === "attended" ? "Send thank-you" : "Send missed-you email"}
                      </Button>
                    )}
                  </span>
                }
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-[160px_1fr]">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-10 rounded-xl border-white/15 bg-white/5 text-sm" data-testid="dialog-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="regNotes">Internal notes</Label>
                <Textarea id="regNotes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} className="rounded-xl border-white/15 bg-white/5 text-sm" placeholder="Follow-up notes for the team…" data-testid="dialog-notes-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose} className="rounded-full text-xs" data-testid="dialog-cancel-btn">Cancel</Button>
              <Button onClick={() => onSave({ status, notes })} className="admin-gold-btn rounded-full px-6 text-xs font-bold" data-testid="dialog-save-btn">Save changes</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
